// src/pages/Dashboard/DashboardCards.js
import React, { useState, useEffect, useRef } from "react"
import CountUp from "react-countup"
import { Card, CardBody, Col, Row, Alert } from "reactstrap" // Added Alert for better error display
import { getTotals } from "../../helpers/api_helper"

const DashboardCards = ({ filters = {} }) => {
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState(null)
  const prevFiltersRef = useRef(null) // Previous filters ko track karne ke liye

  useEffect(() => {
    // Filters ko JSON string mein convert kar ke compare
    const currentFiltersStr = JSON.stringify(filters)
    const prevFiltersStr = JSON.stringify(prevFiltersRef.current)

    // Agar filters same hain to skip (no re-fetch)
    if (currentFiltersStr === prevFiltersStr) {
      return
    }

    // Update previous reference
    prevFiltersRef.current = { ...filters } // shallow copy

    const loadFilteredTotals = async () => {
      // Sirf pehle baar full loading dikhao
      if (!stats.length) {
        setLoading(true)
      }
      setErrorMsg(null)

      try {
        // getTotals ko current filters pass kar rahe hain
        const res = await getTotals(filters)

        console.log("[DashboardCards] getTotals response:", res)

        if (res.success) {
          const breakdown = res.breakdown || {}
          const totals = res.totals || {}

          const safeNumber = val => parseFloat(val) || 0

          const updatedStats = [
            {
              label: "Total Withdraw",
              value: safeNumber(breakdown.withdraw),
              decimals: 2,
              trend: "+8.4",
              trendClass: "success",
              trendIcon: "ri-arrow-up-line",
              bgColor: "danger",
              period: "this period",
            },
            {
              label: "Total Added",
              value: safeNumber(breakdown.added_money),
              decimals: 2,
              trend: "+22.1",
              trendClass: "success",
              trendIcon: "ri-arrow-up-line",
              bgColor: "success",
              period: "this period",
            },
            {
              label: "Total Debit",
              value: safeNumber(totals.total_debit),
              decimals: 2,
              trend: "-5.6",
              trendClass: "danger",
              trendIcon: "ri-arrow-down-line",
              bgColor: "warning",
              period: "this period",
            },
            {
              label: "Total Credit",
              value: safeNumber(totals.total_credit),
              decimals: 2,
              trend: "+15.9",
              trendClass: "success",
              trendIcon: "ri-arrow-up-line",
              bgColor: "primary",
              period: "this period",
            },
          ]

          setStats(updatedStats)
        } else {
          setErrorMsg(res.error || "Failed to load totals")
          setStats([
            {
              label: "Total Withdraw",
              value: 0,
              decimals: 2,
              trend: "N/A",
              trendClass: "secondary",
              trendIcon: "ri-equal-line",
              bgColor: "danger",
              period: "all time",
            },
            {
              label: "Total Added",
              value: 0,
              decimals: 2,
              trend: "N/A",
              trendClass: "secondary",
              trendIcon: "ri-equal-line",
              bgColor: "success",
              period: "all time",
            },
            {
              label: "Total Debit",
              value: 0,
              decimals: 2,
              trend: "N/A",
              trendClass: "secondary",
              trendIcon: "ri-equal-line",
              bgColor: "warning",
              period: "all time",
            },
            {
              label: "Total Credit",
              value: 0,
              decimals: 2,
              trend: "N/A",
              trendClass: "secondary",
              trendIcon: "ri-equal-line",
              bgColor: "primary",
              period: "all time",
            },
          ])
        }
      } catch (err) {
        console.error("Totals fetch error:", err)
        setErrorMsg(
          "Failed to load totals. Please check your connection and try again."
        )
      } finally {
        setLoading(false)
      }
    }

    loadFilteredTotals()
  }, [filters]) // dependency rakhi hai, lekin ref se skip kar rahe hain

  if (loading && !stats.length) {
    return (
      <Row className="justify-content-center">
        <Col xl="12" className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading totals...</span>
          </div>
          <p className="mt-2 text-muted">Fetching dashboard stats...</p>
        </Col>
      </Row>
    )
  }

  if (errorMsg) {
    return (
      <Row className="justify-content-center">
        <Col xl="12">
          <Alert color="danger" className="d-flex align-items-center">
            <i className="ri-error-warning-line fs-4 me-2"></i>{" "}
            {/* Error icon added */}
            <div>
              <h5 className="alert-heading mb-1">Error Loading Data</h5>
              <p className="mb-0">{errorMsg}</p>
              <p className="mb-0">Showing default values for now.</p>
            </div>
          </Alert>
        </Col>
      </Row>
    )
  }

  return (
    <Row className="g-4">
      {" "}
      {/* Added gutter for better spacing */}
      {stats.map((item, index) => (
        <Col xl="3" lg="6" md="6" sm="12" key={index}>
          <Card className="shadow border-0 h-100 card-hover border-start border-5 border-primary overflow-hidden">
            {" "}
            {/* Added overflow-hidden to prevent content overflow */}
            <CardBody className="p-4 d-flex flex-column justify-content-between">
              <div>
                <p className="text-muted fw-medium mb-1 text-truncate">
                  {item.label}
                </p>
                <h4 className="mb-0 fw-bold fs-3 ff-secondary text-dark text-nowrap overflow-hidden text-truncate">
                  {" "}
                  {/* Added text-truncate for large values */}₹{" "}
                  <CountUp
                    start={0}
                    end={item.value}
                    duration={1.5}
                    separator=","
                    decimals={item.decimals}
                    delay={0.2}
                  />
                </h4>
              </div>

              <div className="d-flex align-items-center mt-3">
                <span
                  className={`badge bg-soft-${item.trendClass} text-${item.trendClass} fs-13 px-2 py-1 me-2`}
                >
                  <i className={`${item.trendIcon} align-middle me-1`}></i>
                  {item.trend}%
                </span>
                <span className="text-muted fs-13">{item.period}</span>
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  )
}

export default DashboardCards
